import { useEffect, useRef } from "react";
import * as THREE from "three";

export default function GlobeScene({ className }) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const earthRef = useRef(null);
  const frameRef = useRef(null);
  const isDragging = useRef(false);
  const previousMouse = useRef({ x: 0, y: 0 });
  const rotationVelocity = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      45,
      container.clientWidth / container.clientHeight,
      0.1,
      100
    );
    camera.position.z = 5.5;
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
    directionalLight.position.set(5, 3, 5);
    scene.add(directionalLight);

    const backLight = new THREE.DirectionalLight(0xffffff, 0.3);
    backLight.position.set(-3, -1, -3);
    scene.add(backLight);

    // Earth sphere
    const geometry = new THREE.SphereGeometry(2, 64, 64);
    const fallbackMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a6b5a,
      metalness: 0.2,
      roughness: 0.6,
    });

    const earth = new THREE.Mesh(geometry, fallbackMaterial);
    scene.add(earth);
    earthRef.current = earth;

    // Load texture
    const textureLoader = new THREE.TextureLoader();
    textureLoader.crossOrigin = "anonymous";
    textureLoader.load(
      "https://unpkg.com/three-globe@2.31.1/example/img/earth-blue-marble.jpg",
      (texture) => {
        earth.material = new THREE.MeshStandardMaterial({
          map: texture,
          metalness: 0.1,
          roughness: 0.7,
        });
      },
      undefined,
      () => {
        textureLoader.load(
          "https://cdn.jsdelivr.net/npm/three-globe/example/img/earth-blue-marble.jpg",
          (texture) => {
            earth.material = new THREE.MeshStandardMaterial({
              map: texture,
              metalness: 0.1,
              roughness: 0.7,
            });
          }
        );
      }
    );

    // Atmosphere glow
    const atmosphereVertexShader = `
      varying vec3 vNormal;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
    const atmosphereFragmentShader = `
      varying vec3 vNormal;
      void main() {
        float intensity = pow(0.65 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.5);
        gl_FragColor = vec4(0.26, 0.63, 0.46, 1.0) * intensity;
      }
    `;
    const atmosphereMaterial = new THREE.ShaderMaterial({
      vertexShader: atmosphereVertexShader,
      fragmentShader: atmosphereFragmentShader,
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
      transparent: true,
    });
    const atmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(2.3, 64, 64),
      atmosphereMaterial
    );
    scene.add(atmosphere);

    // Mouse interaction
    const onMouseDown = (e) => {
      isDragging.current = true;
      previousMouse.current = { x: e.clientX, y: e.clientY };
      rotationVelocity.current = { x: 0, y: 0 };
    };
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const dx = e.clientX - previousMouse.current.x;
      const dy = e.clientY - previousMouse.current.y;
      rotationVelocity.current = { x: dy * 0.003, y: dx * 0.003 };
      previousMouse.current = { x: e.clientX, y: e.clientY };
    };
    const onMouseUp = () => {
      isDragging.current = false;
    };

    // Touch interaction
    const onTouchStart = (e) => {
      if (e.touches.length === 1) {
        isDragging.current = true;
        previousMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        rotationVelocity.current = { x: 0, y: 0 };
      }
    };
    const onTouchMove = (e) => {
      if (!isDragging.current || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - previousMouse.current.x;
      const dy = e.touches[0].clientY - previousMouse.current.y;
      rotationVelocity.current = { x: dy * 0.003, y: dx * 0.003 };
      previousMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };
    const onTouchEnd = () => {
      isDragging.current = false;
    };

    // Zoom
    const onWheel = (e) => {
      e.preventDefault();
      camera.position.z = Math.max(3.5, Math.min(8, camera.position.z + e.deltaY * 0.005));
    };

    const canvas = renderer.domElement;
    canvas.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    canvas.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd);
    canvas.addEventListener("wheel", onWheel, { passive: false });

    // Resize handler
    const onResize = () => {
      if (!container) return;
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener("resize", onResize);

    // Animate
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);

      if (!isDragging.current) {
        earth.rotation.y += 0.002;
      }

      if (rotationVelocity.current.y !== 0 || rotationVelocity.current.x !== 0) {
        earth.rotation.y += rotationVelocity.current.y;
        earth.rotation.x += rotationVelocity.current.x;
        earth.rotation.x = Math.max(
          -Math.PI / 3,
          Math.min(Math.PI / 3, earth.rotation.x)
        );
        if (!isDragging.current) {
          rotationVelocity.current.x *= 0.95;
          rotationVelocity.current.y *= 0.95;
          if (Math.abs(rotationVelocity.current.x) < 0.0001) rotationVelocity.current.x = 0;
          if (Math.abs(rotationVelocity.current.y) < 0.0001) rotationVelocity.current.y = 0;
        }
      }

      atmosphere.rotation.y = earth.rotation.y;
      atmosphere.rotation.x = earth.rotation.x;

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(frameRef.current);
      canvas.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      canvas.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      canvas.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      geometry.dispose();
      fallbackMaterial.dispose();
      atmosphereMaterial.dispose();
      if (container && renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      data-testid="globe-container"
      style={{ cursor: "grab" }}
    />
  );
}
